import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';

export const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'CollectABILITY Privacy Policy | Service Provider Data Practices';

    // Update meta description
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', 'Learn how CollectABILITY handles limited public-record data as a CPRA-compliant service provider. We do not sell or share personal information.');
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">CollectABILITY Privacy Policy</h1>
        <p className="text-slate-600 mb-12">Last Updated: October 13, 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction and Acceptance</h2>
            <p className="text-slate-700 mb-4">
              Welcome to CollectABILITY, LLC ("CollectABILITY," "we," "our," or "us"). This Privacy Policy explains how we handle information in connection with your use of our website (collectability.ai) and related applications and services (collectively, the "Services").
            </p>
            <p className="text-slate-700 mb-4">
              By accessing or using our Services, you acknowledge that you have read and understood this Privacy Policy. If you do not agree, please discontinue use of the Services.
            </p>
            <p className="text-slate-700 mb-4">
              CollectABILITY acts primarily as a service provider (as defined under the California Privacy Rights Act (CPRA) and similar state laws) or processor (as defined under GDPR and other international frameworks). We do not operate as a data broker and do not sell or share personal information for cross-context behavioral advertising.
            </p>
            <p className="text-slate-700 mb-4">
              We process limited data solely on behalf of our business customers, and only as necessary to deliver our contracted Services, which may include accessing public record data from licensed third-party providers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Handle</h2>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.1 Information You Provide Directly</h3>
            <p className="text-slate-700 mb-4">
              We may receive information from you or your organization when you:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>Request access to our Services or create an account;</li>
              <li>Communicate with us for support or business purposes; or</li>
              <li>Provide contact information (such as name, business email, or phone number).</li>
            </ul>
            <p className="text-slate-700 mb-4">
              This information is used only to respond to your inquiries, manage your account, or fulfill our contractual obligations.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.2 Information from Public Sources and Partners</h3>
            <p className="text-slate-700 mb-4">
              Certain limited data displayed in our products originates from public records or licensed third-party data sources. CollectABILITY does not alter or repurpose such data outside the scope of your request, and we do not maintain a consumer database for resale.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.3 Automatically Collected Technical Information</h3>
            <p className="text-slate-700 mb-4">
              When you visit our website, we may collect standard technical data such as your IP address, browser type, and device identifiers through cookies or analytics tools. This information is used only to maintain security, improve performance, and analyze usage trends.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Purpose of Processing</h2>
            <p className="text-slate-700 mb-4">
              We process information solely to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>Provide, maintain, and support the Services;</li>
              <li>Respond to user or client inquiries;</li>
              <li>Monitor system functionality and security;</li>
              <li>Comply with applicable law and contractual requirements; and</li>
              <li>Perform internal analytics that do not identify individuals.</li>
            </ul>
            <p className="text-slate-700 mb-4">
              We do not use personal data for profiling, targeted advertising, or automated decision-making that produces legal or similarly significant effects.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Our Role as a Service Provider / Processor</h2>
            <p className="text-slate-700 mb-4">
              CollectABILITY processes data on behalf of its business customers.
            </p>
            <p className="text-slate-700 mb-4">
              We act only under their documented instructions, and we do not:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>Combine client data with other datasets for unrelated purposes;</li>
              <li>Retain, use, or disclose personal information for our own benefit; or</li>
              <li>Sell or share personal information with third parties.</li>
            </ul>
            <p className="text-slate-700 mb-4">
              If you are an individual whose information appears in materials processed through CollectABILITY, please direct any data access or deletion requests to the business entity that engaged our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Cookies and Analytics</h2>
            <p className="text-slate-700 mb-4">
              We use functional and performance cookies to operate and secure our website. We do not use third-party ad cookies or engage in behavioral advertising.
            </p>
            <p className="text-slate-700 mb-4">
              You may adjust cookie preferences through your browser settings. Rejecting cookies may affect some features of the website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-slate-700 mb-4">
              We may disclose limited information:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>To service providers that perform infrastructure, hosting, or analytics services under written contracts restricting further use;</li>
              <li>As required by law (e.g., to respond to lawful subpoenas or regulatory obligations); or</li>
              <li>In corporate transactions, if CollectABILITY is acquired or merged, with notice provided to affected clients.</li>
            </ul>
            <p className="text-slate-700 mb-4">
              We do not sell, share, or trade personal information for monetary or cross-context advertising purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Security and Retention</h2>
            <p className="text-slate-700 mb-4">
              We use commercially reasonable safeguards to protect data from unauthorized access, disclosure, or misuse.
            </p>
            <p className="text-slate-700 mb-4">
              No online system is completely secure, and we cannot guarantee absolute security.
            </p>
            <p className="text-slate-700 mb-4">
              We retain information only for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li>Fulfill our contractual or legal obligations; or</li>
              <li>Support ongoing legitimate business operations (e.g., auditing, fraud prevention).</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Once no longer required, data is securely deleted or de-identified.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Consumer Privacy Rights</h2>
            <p className="text-slate-700 mb-4">
              Depending on your jurisdiction, you may have rights such as:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700">
              <li><strong>Access / Portability</strong> – to know what information we hold and receive a copy in portable format;</li>
              <li><strong>Correction / Deletion</strong> – to request that inaccurate data be corrected or deleted;</li>
              <li><strong>Restriction / Objection</strong> – to limit processing under certain circumstances;</li>
              <li><strong>Opt-Out of Sale or Sharing</strong> – not applicable, as CollectABILITY does not sell or share data; and</li>
              <li><strong>Non-Discrimination</strong> – you will not receive different pricing or service levels for exercising privacy rights.</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Requests can be submitted via <a href="mailto:privacy@collectability.ai" className="text-blue-600 hover:text-slate-600">privacy@collectability.ai</a>.
            </p>
            <p className="text-slate-700 mb-4">
              For verification, we may request reasonable information confirming your identity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. International Data Transfers</h2>
            <p className="text-slate-700 mb-4">
              For clients or users in the European Economic Area, United Kingdom, or Canada, we comply with applicable data-protection requirements.
            </p>
            <p className="text-slate-700 mb-4">
              Transfers of personal data outside these regions occur only under approved legal mechanisms such as adequacy decisions or standard contractual clauses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Information</h2>
            <p className="text-slate-700 mb-4">
              Our Services are intended for use by adults (18 years or older).
            </p>
            <p className="text-slate-700 mb-4">
              We do not knowingly collect or process any personal information from minors.
            </p>
            <p className="text-slate-700 mb-4">
              If you believe a minor's data has been submitted, contact <a href="mailto:privacy@collectability.ai" className="text-blue-600 hover:text-slate-600">privacy@collectability.ai</a> for prompt removal.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Third-Party Websites</h2>
            <p className="text-slate-700 mb-4">
              Our website may link to other websites or services. We are not responsible for the content or privacy practices of those third parties. Please review their respective privacy policies before providing any information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Updates to This Policy</h2>
            <p className="text-slate-700 mb-4">
              We may revise this Privacy Policy periodically to reflect changes in our practices or legal obligations. Updates will be posted on this page with a new "Last Updated" date.
            </p>
            <p className="text-slate-700 mb-4">
              Continued use of the Services after posting means you accept the revised policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Contact Information</h2>
            <p className="text-slate-700 mb-4">
              For privacy questions or to exercise your rights:
            </p>
            <p className="text-slate-700 mb-4">
              CollectABILITY, LLC<br />
              Attn: Privacy Officer<br />
              Email: <a href="mailto:privacy@collectability.ai" className="text-blue-600 hover:text-slate-600">privacy@collectability.ai</a><br />
              Website: <a href="https://collectability.ai" className="text-blue-600 hover:text-slate-600">https://collectability.ai</a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-200 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Logo & About */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-slate-700 text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:support@collectability.ai"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors"
                >
                  support@collectability.ai
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Web Search
                  </Link>
                </li>
                <li>
                  <a href="/#features" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Features
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/#contact" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 space-y-3">
            <div className="p-4 bg-slate-300 rounded-lg">
              <p className="text-slate-700 text-sm leading-relaxed">
                <strong className="text-slate-900">Service Provider Statement:</strong> CollectABILITY, LLC operates solely as a service provider and data processor on behalf of its business clients. We do not sell or share personal information. A copy of our Service Provider Addendum is available to clients upon request at{' '}
                <a href="mailto:privacy@collectability.ai" className="text-collectability-blue hover:text-collectability-blue-dark transition-colors">
                  privacy@collectability.ai
                </a>.
              </p>
            </div>
            <FCRAFooterText />
            <p className="text-slate-700 text-sm text-center">
              &copy; 2024 CollectABILITY, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};


